import type { VenueDataSourceValue } from "../types/venue";
import { getVenueDataSource, hasVenueData } from "./maps";

/**
 * The signals a venue's provenance badge resolves from.
 *
 * `Venue` spells the review count `reviewsDetailsSubmitted` and `VenueDetails`
 * spells it `reviewDetailsSubmitted`, so both are accepted and reconciled here.
 * Every field is optional: callers pass whichever payload they hold.
 */
export type VenueDataSourceInput = {
  reviewsDetailsSubmitted?: number;
  reviewDetailsSubmitted?: number;
  hasExperiences?: boolean;
  hasAlerts?: boolean;
  hasTeamTags?: boolean;
  hasGeneralUserTags?: boolean;
  hasGeneralUserPhotos?: boolean;
  venueDataSource?: VenueDataSourceValue;
  /**
   * The backend's `business_verified` enum, verbatim. Only read when the payload
   * carries no resolved `venueDataSource` — @see getVenueDataSource.
   */
  businessVerified?: string | null;
  latestReviewDateUpdated?: string;
};

/**
 * Reconciles the two field namings before the resolver sees them.
 *
 * Dropping `hasGeneralUserTags`, `venueDataSource` or `businessVerified` makes
 * `getVenueDataSource` fall through to a later rule, so a claimed venue reads
 * "Community Contributed" and a mixed-contributor one "Sociability Verified" —
 * which is why every surface goes through this rather than building its own
 * object.
 */
const dataSourceSignals = (venue: VenueDataSourceInput) => ({
  reviewsDetailsSubmitted:
    venue.reviewsDetailsSubmitted ?? venue.reviewDetailsSubmitted ?? 0,
  hasExperiences: venue.hasExperiences,
  hasAlerts: venue.hasAlerts,
  hasTeamTags: venue.hasTeamTags,
  hasGeneralUserTags: venue.hasGeneralUserTags,
  hasGeneralUserPhotos: venue.hasGeneralUserPhotos,
  venueDataSource: venue.venueDataSource,
  businessVerified: venue.businessVerified,
});

/**
 * The provenance a venue's badge displays, or "none" when it shows nothing.
 *
 * Shared by web, the embeddable widget, the business dashboard and the app, so
 * the same venue can never read differently in two places. A venue with no
 * contributed data at all gets no badge, whatever the tag flags say.
 */
export const resolveVenueDataSource = (
  venue: VenueDataSourceInput | null | undefined,
): VenueDataSourceValue => {
  if (!venue) return "none";
  const signals = dataSourceSignals(venue);
  return hasVenueData(signals) ? getVenueDataSource(signals) : "none";
};

/** Whether the badge renders anything for this venue. */
export const hasVenueDataSourceBadge = (
  venue: VenueDataSourceInput | null | undefined,
): boolean => resolveVenueDataSource(venue) !== "none";

/**
 * Whether the venue carries the "Sociability Verified" badge. A verified venue
 * has been checked by the team, so its tags stop being presented as
 * machine-written — @see isSmartTaggedSpace.
 */
export const isVenueSociabilityVerified = (
  venue: VenueDataSourceInput | null | undefined,
): boolean => resolveVenueDataSource(venue) === "team";
